/**
 * PlanRail — the plan "coming together" visual on the PRE-APPROVAL review
 * (spec §4: a vertical bar shows up, then circles show up, then each step's
 * text is written out — and only THEN is the user prompted to review/confirm).
 *
 * This is the review-stage counterpart to StepRail (the post-approval
 * execution timeline): the SAME bar + circle shape, but the circles are
 * neutral grey "planned" markers — no execution status colour and no pulse —
 * because nothing is running yet.
 *
 * Reveal mechanism — a CSS KEYFRAME animation (`animate-fade-in-up`), NOT a
 * mount-time opacity transition (a transition only animates if the browser
 * paints the opacity-0 frame before the class flips, which a fresh mount
 * usually skips — the "it looks instant" bug). A keyframe plays from first
 * paint, so the per-element `animation-delay` staggers the reveal reliably;
 * `fill-mode: both` holds each element hidden during its delay.
 *
 * The gaps between steps are RANDOMISED (3-5s) so the plan reads as the agent
 * thinking each step through at a human pace. The reveal plays ONCE, the first
 * time the plan appears: the `animate` prop (driven by the caller's
 * "already revealed?" state) turns the staged reveal OFF on later mounts — e.g.
 * after the user opens the editor and goes back — so the plan is simply THERE,
 * not re-animated. `motion-safe:` gated, so reduced-motion users get it instant.
 */
import { useEffect, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';

const BASE_MS = 500;           // first step appears shortly after the bar
const MIN_GAP_MS = 3000;       // shortest gap between consecutive steps (3s)
const MAX_GAP_MS = 5000;       // longest gap between consecutive steps (5s)
const CARD_AFTER_CIRCLE_MS = 300; // a step's text follows its circle by this
const FADE_MS = 450;           // how long each element's fade-in-up runs

const REVEAL_CLASS = 'motion-safe:animate-fade-in-up';
function revealStyle(delayMs: number): CSSProperties {
  return {
    animationDelay: `${delayMs}ms`,
    animationDuration: `${FADE_MS}ms`,
    animationFillMode: 'both',
  };
}

export function PlanRail({
  items,
  listTestId,
  onRevealed,
  animate = true,
}: {
  items: ReactNode[];
  listTestId?: string;
  /** Fired once, after the LAST step's text has finished fading in — lets the
   *  caller keep the agent in a "thinking/writing" state until the plan has
   *  actually finished appearing, then flip to "complete, please review". */
  onRevealed?: (() => void) | undefined;
  /** When false, the plan renders fully and statically (no staged reveal) —
   *  used once the plan has already been revealed, so re-mounting the rail
   *  (e.g. leaving the editor) does not replay the animation. */
  animate?: boolean;
}) {
  // Randomised cumulative delay per step, stable for this mount.
  const circleDelays = useMemo(() => {
    const delays: number[] = [];
    let acc = BASE_MS;
    for (let i = 0; i < items.length; i += 1) {
      delays.push(acc);
      acc += MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS);
    }
    return delays;
  }, [items.length]);

  // When the last step's card has finished its fade-in, the plan is fully
  // "written out" — signal the caller so it can drop the thinking state. Only
  // meaningful while animating; a static (already-revealed) render is done now.
  const lastCircle = circleDelays.length ? circleDelays[circleDelays.length - 1]! : BASE_MS;
  const totalMs = lastCircle + CARD_AFTER_CIRCLE_MS + FADE_MS;
  useEffect(() => {
    if (!onRevealed) return undefined;
    if (!animate) { onRevealed(); return undefined; }
    const id = setTimeout(onRevealed, totalMs);
    return () => clearTimeout(id);
  }, [animate, onRevealed, totalMs]);

  const revealClass = animate ? REVEAL_CLASS : '';
  const styleFor = (delayMs: number): CSSProperties | undefined => (animate ? revealStyle(delayMs) : undefined);

  return (
    <div className="relative" data-testid="plan-rail">
      {/* The vertical bar appears first (no delay). */}
      <span
        aria-hidden="true"
        className={`absolute left-[11px] top-2 bottom-2 w-px bg-slate-200 ${revealClass}`}
        style={styleFor(0)}
      />
      <ol className="relative space-y-3" data-testid={listTestId}>
        {items.map((node, i) => {
          const circleDelay = circleDelays[i] ?? BASE_MS;
          const cardDelay = circleDelay + CARD_AFTER_CIRCLE_MS;
          return (
            <li key={i} className="relative flex gap-3">
              <span
                aria-hidden="true"
                data-testid={`plan-rail-circle-${i + 1}`}
                // Grey "planned" marker (not an execution-status colour).
                className={`relative z-10 mt-4 h-[22px] w-[22px] shrink-0 rounded-full border-2 bg-slate-300 border-slate-300 ${revealClass}`}
                style={styleFor(circleDelay)}
              />
              <div
                className={`min-w-0 flex-1 ${revealClass}`}
                style={styleFor(cardDelay)}
              >
                {node}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default PlanRail;
